import React, { useEffect, useState } from "react";
import * as FiIcons from "react-icons/fi";
import {
  SettingsActions,
  SettingsCard,
  SettingsField,
  SettingsNotice,
} from "./SettingsPanel";

const { FiUser } = FiIcons;

const initials = (profile) =>
  `${profile?.first_name?.[0] || ""}${profile?.last_name?.[0] || ""}`
    .toUpperCase() || "P";

export default function MyProfileSettings({
  profile,
  email,
  role,
  userId,
  onSaved,
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({
    first_name: "",
    last_name: "",
    phone: "",
  });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    setDraft({
      first_name: profile?.first_name || "",
      last_name: profile?.last_name || "",
      phone: profile?.phone || "",
    });
    setEditing(false);
    setMessage(null);
  }, [profile, userId]);

  const startEditing = () => {
    setDraft({
      first_name: profile?.first_name || "",
      last_name: profile?.last_name || "",
      phone: profile?.phone || "",
    });
    setEditing(true);
    setMessage(null);
  };

  const cancel = () => {
    startEditing();
    setEditing(false);
  };

  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    setMessage(null);

    try {
      const updated = await onSaved(draft);
      setDraft({
        first_name: updated.first_name || "",
        last_name: updated.last_name || "",
        phone: updated.phone || "",
      });
      setEditing(false);
      setMessage({
        kind: "success",
        text: "Your profile changes were saved.",
      });
    } catch (error) {
      setMessage({
        kind: "error",
        text: error.message || "Unable to save your profile.",
      });
    } finally {
      setSaving(false);
    }
  };

  if (!profile) {
    return (
      <SettingsCard
        title="My Profile"
        description="Your personal details and account role."
        icon={FiUser}
      >
        <SettingsNotice kind="error">
          Your profile record is unavailable. Personal profile editing is disabled until it can be loaded.
        </SettingsNotice>
      </SettingsCard>
    );
  }

  return (
    <SettingsCard
      title="My Profile"
      description="Personal information for your ProfitYo account."
      icon={FiUser}
    >
      {message && (
        <div className="mb-4">
          <SettingsNotice kind={message.kind}>
            {message.text}
          </SettingsNotice>
        </div>
      )}

      <div className="mb-6 flex items-center gap-4">
        {profile.avatar_url ? (
          <img
            src={profile.avatar_url}
            alt=""
            className="h-14 w-14 rounded-2xl object-cover"
          />
        ) : (
          <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-100 text-lg font-bold text-emerald-800">
            {initials(profile)}
          </span>
        )}
        <div className="min-w-0">
          <p className="font-semibold text-slate-900">
            {[profile.first_name, profile.last_name].filter(Boolean).join(" ") || "Name not provided"}
          </p>
          <p className="mt-1 break-all text-sm text-slate-500">
            {email || "Email unavailable"}
          </p>
        </div>
      </div>

      <form onSubmit={save} className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <SettingsField label="First Name">
            {editing ? (
              <input
                autoComplete="given-name"
                value={draft.first_name}
                onChange={(event) =>
                  setDraft({ ...draft, first_name: event.target.value })
                }
                className="mt-1.5 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:border-emerald-600 focus:outline-none focus:ring-2 focus:ring-emerald-100"
              />
            ) : (
              <span className="mt-1.5 block rounded-xl bg-slate-50 px-3 py-2.5 text-sm text-slate-700">
                {profile.first_name || "Not provided"}
              </span>
            )}
          </SettingsField>

          <SettingsField label="Last Name">
            {editing ? (
              <input
                autoComplete="family-name"
                value={draft.last_name}
                onChange={(event) =>
                  setDraft({ ...draft, last_name: event.target.value })
                }
                className="mt-1.5 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:border-emerald-600 focus:outline-none focus:ring-2 focus:ring-emerald-100"
              />
            ) : (
              <span className="mt-1.5 block rounded-xl bg-slate-50 px-3 py-2.5 text-sm text-slate-700">
                {profile.last_name || "Not provided"}
              </span>
            )}
          </SettingsField>

          <SettingsField label="Email Address" hint="Email changes are managed through a verified Supabase Auth flow.">
            <span className="mt-1.5 block min-h-10 break-all rounded-xl bg-slate-50 px-3 py-2.5 text-sm text-slate-700">
              {email || "Unavailable"}
            </span>
          </SettingsField>

          <SettingsField label="Phone Number">
            {editing ? (
              <input
                type="tel"
                autoComplete="tel"
                value={draft.phone}
                onChange={(event) =>
                  setDraft({ ...draft, phone: event.target.value })
                }
                className="mt-1.5 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:border-emerald-600 focus:outline-none focus:ring-2 focus:ring-emerald-100"
              />
            ) : (
              <span className="mt-1.5 block rounded-xl bg-slate-50 px-3 py-2.5 text-sm text-slate-700">
                {profile.phone || "Not provided"}
              </span>
            )}
          </SettingsField>

          <SettingsField label="Account Role">
            <span className="mt-1.5 block rounded-xl bg-slate-50 px-3 py-2.5 text-sm capitalize text-slate-700">
              {(role || profile.role || "Not provided").replaceAll("_", " ")}
            </span>
          </SettingsField>
        </div>

        {editing ? (
          <SettingsActions
            onCancel={cancel}
            saving={saving}
            disabled={false}
          />
        ) : (
          <div className="flex justify-end border-t border-slate-100 pt-4">
            <button
              type="button"
              onClick={startEditing}
              className="rounded-xl bg-emerald-700 px-4 py-2.5 text-sm font-semibold text-white hover:bg-emerald-800"
            >
              Edit profile
            </button>
          </div>
        )}
      </form>
    </SettingsCard>
  );
}